/**
 * The welcome wizard can open while the account is still being deployed (right after the code is verified).
 * Only the sign-in token is needed to ask the questions; nothing is written to the account until it is deployed.
 * These helpers build what that early session needs from the signup data (no network).
 */

/** sessionStorage flag: this tab is in the early wizard (token only, account still deploying). */
export const EARLY_WIZARD_FLAG = 'early-wizard';

export interface EarlyIdentity {
  email: string;
  password: string;
  userId: string;
  businessId: string | number;
  businessNameWithDomain: string;
  firstName: string;
  lastName: string;
}

/** The ids and credentials signup saved: register's reply (business) and the signup form (personal). Null when something needed is missing. */
export function readEarlyIdentity(personal: any, business: any): EarlyIdentity | null {
  const email = String(personal?.email ?? business?.email ?? '').trim();
  const password = String(personal?.password ?? '').trim();
  const userId = String(personal?.userID ?? business?.userID ?? '').trim();
  const businessId = business?.businessId;
  const businessNameWithDomain = String(business?.businessNameWithDomain ?? business?.businessName ?? '').trim();
  if (!email || !password || !userId || !businessId || !businessNameWithDomain) return null;
  return {
    email,
    password,
    userId,
    businessId,
    businessNameWithDomain,
    firstName: String(personal?.firstName ?? ''),
    lastName: String(personal?.lastName ?? '')
  };
}

/** The token from a sign-in reply (the account may still say it is deploying; the token is all that is needed). */
export function earlyLoginToken(body: any): { token: string; expiresIn: number; refreshToken: string } | null {
  const data = body?.data ?? body;
  const token = data?.access_token;
  if (!token) return null;
  return { token: String(token), expiresIn: Number(data.expires_in) || 7200, refreshToken: String(data.refresh_token ?? '') };
}

/**
 * The smallest `GlobalService.userInfo` the welcome page reads before the account is deployed. `isEarly` marks it,
 * so the page can tell it from the real one the full sign-in loads later.
 */
export function buildEarlyUserInfo(identity: EarlyIdentity): any {
  return {
    isEarly: true,
    userID: identity.userId,
    email: identity.email,
    username: identity.email,
    firstName: identity.firstName,
    lastName: identity.lastName,
    userLocations: [],
    businessSetup: [{
      businessId: identity.businessId,
      businessNameWithDomain: identity.businessNameWithDomain,
      businessName: identity.businessNameWithDomain
    }]
  };
}
